import { FC, ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";

import { QueryStats } from "@mui/icons-material";
import { Box, Checkbox, FormControlLabel, Stack, Tooltip, Typography } from "@mui/material";

import { EmptyPlaceholder } from "../../../../components/common/empty-placeholder";
import { getAnomalyStripeColor } from "../scheme/components/state-and-forecast/utils";
import { IAnomalyMeasureTrendData, IAnomalyMeasureTrendPoint, ISelectedForecastTrend } from "./interfaces";
import {
  buildChronogramTicks,
  CHRONOGRAM_NAME_CELL_WIDTH,
  FORECAST_CHART_PLOT_PADDING,
  FORECAST_EXTENSION_BOUNDARY_COLOR,
  FORECAST_TIMELINE_GRID_SX,
  FORECAST_TIMELINE_PLOT_CELL_SX,
  getForecastTimestampOffset,
  getForecastTimeDomain,
  getRenderableForecastTrends,
  groupForecastTrends,
  hasTrendLineData,
  isTciForecastTrend,
} from "./utils";

const CHART_HEIGHT = 320;
const DEFAULT_CHART_WIDTH = 960;
const PADDING = {
  top: FORECAST_CHART_PLOT_PADDING.top,
  right: FORECAST_CHART_PLOT_PADDING.right,
  bottom: FORECAST_CHART_PLOT_PADDING.bottom,
  left: FORECAST_CHART_PLOT_PADDING.left,
};
const PLOT_HEIGHT = CHART_HEIGHT - PADDING.top - PADDING.bottom;
const AXIS_LABEL_SX = {
  fontSize: 11,
  lineHeight: "14px",
  color: "#303544",
  whiteSpace: "nowrap",
} as const;

const getXAxisTickLabelSx = (index: number, total: number, leftPercent: number) => {
  if (index === 0) {
    return { left: 0, transform: "none" };
  }

  if (index === total - 1) {
    return { left: "100%", transform: "translateX(-100%)" };
  }

  return { left: `${leftPercent}%`, transform: "translateX(-50%)" };
};

const MEASURE_TREND_COLORS = ["#2f80ed", "#e67e22", "#8e44ad", "#16a085", "#c0392b", "#27ae60", "#d35400", "#2980b9"];

interface ITrendPointWithPosition extends IAnomalyMeasureTrendPoint {
  x: number;
  y: number;
}

interface IMeasureTrendChartSeries {
  key: string;
  label: string;
  color: string;
  data: IAnomalyMeasureTrendData;
}

const parseTime = (value: string | Date): number => new Date(value).getTime();

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const buildLinePoints = (
  points: IAnomalyMeasureTrendPoint[],
  xScale: (value: string) => number,
  yScale: (value: number) => number,
): ITrendPointWithPosition[] =>
  points
    .map(point => ({
      ...point,
      x: xScale(point.Date),
      y: yScale(point.Value),
    }))
    .filter(point => Number.isFinite(point.x) && Number.isFinite(point.y));

const getPolylinePoints = (points: ITrendPointWithPosition[]): string =>
  points.map(point => `${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(" ");

const getAnomalyPointOnLine = (
  date: string,
  points: ITrendPointWithPosition[],
  xScale: (value: string) => number,
): ITrendPointWithPosition | null => {
  const target = parseTime(date);

  if (!Number.isFinite(target) || points.length === 0) {
    return null;
  }

  const sorted = [...points].sort((left, right) => parseTime(left.Date) - parseTime(right.Date));
  const exact = sorted.find(point => parseTime(point.Date) === target);

  if (exact) {
    return { ...exact, x: xScale(date), y: exact.y };
  }

  let left: ITrendPointWithPosition | null = null;
  let right: ITrendPointWithPosition | null = null;

  for (const point of sorted) {
    const pointTime = parseTime(point.Date);

    if (pointTime < target) {
      left = point;
      continue;
    }

    right = point;
    break;
  }

  if (left && right) {
    const leftTime = parseTime(left.Date);
    const rightTime = parseTime(right.Date);
    const ratio = rightTime === leftTime ? 0 : (target - leftTime) / (rightTime - leftTime);

    return {
      Date: date,
      Value: left.Value + (right.Value - left.Value) * ratio,
      x: xScale(date),
      y: left.y + (right.y - left.y) * ratio,
    };
  }

  const fallback = left ?? right;

  return fallback ? { ...fallback, Date: date, x: xScale(date), y: fallback.y } : null;
};

const buildYTicks = (minValue: number, maxValue: number): number[] => {
  const ticksCount = 4;
  const diff = maxValue - minValue;

  return Array.from({ length: ticksCount + 1 }, (_, index) => maxValue - (diff * index) / ticksCount);
};

const getChartDomain = (
  datasets: IAnomalyMeasureTrendData[],
  startTimestamp: string | Date,
  endTimestamp: string | Date,
): { minTime: number; maxTime: number; minValue: number; maxValue: number } | null => {
  const timeDomain = getForecastTimeDomain(startTimestamp, endTimestamp);
  const values = datasets
    .flatMap(data => [...data.history, ...data.forecast])
    .map(point => point.Value)
    .filter(Number.isFinite);

  if (!timeDomain || values.length === 0) {
    return null;
  }

  const rawMinValue = Math.min(...values);
  const rawMaxValue = Math.max(...values);
  const valuePadding = rawMaxValue === rawMinValue ? 1 : (rawMaxValue - rawMinValue) * 0.12;

  return {
    minTime: timeDomain.minTime,
    maxTime: timeDomain.maxTime,
    minValue: rawMinValue - valuePadding,
    maxValue: rawMaxValue + valuePadding,
  };
};

const getTrendTitle = (data: IAnomalyMeasureTrendData, trend: ISelectedForecastTrend, tciLabel: string): string =>
  isTciForecastTrend(trend) ? `${tciLabel}: ${trend.name}` : data.metadata?.measureName ?? trend.name;

const getMeasurePath = (data: IAnomalyMeasureTrendData): string | null => {
  if (!data.metadata) {
    return null;
  }

  return [data.metadata.pointName, data.metadata.mechanismName, data.metadata.trainName].filter(Boolean).join(" / ");
};

const LegendItem: FC<{ color: string; dashed?: boolean; label: ReactNode }> = ({ color, dashed, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <Box
      sx={{
        width: 24,
        height: 0,
        borderTop: dashed ? `2px dashed ${color}` : `2px solid ${color}`,
      }}
    />
    <Typography sx={{ fontSize: 11, lineHeight: "14px", color: "#5f6472" }}>{label}</Typography>
  </Box>
);

const MeasureTrendChart: FC<{
  ariaLabel: string;
  endTimestamp: string | Date;
  extensionBoundaryTimestamp: string | Date | null;
  formatDate: (date: Date | string) => string;
  formatNumber: IntlShape["formatNumber"];
  formatMessage: IntlShape["formatMessage"];
  series: IMeasureTrendChartSeries[];
  showSeriesLegend?: boolean;
  startTimestamp: string | Date;
}> = ({
  ariaLabel,
  endTimestamp,
  extensionBoundaryTimestamp,
  formatDate,
  formatMessage,
  formatNumber,
  series,
  showSeriesLegend = false,
  startTimestamp,
}) => {
  const rawPatternId = useId();
  const forecastPatternId = `forecast-trend-stripes-${rawPatternId.replace(/:/g, "")}`;
  const plotContainerRef = useRef<HTMLDivElement | null>(null);
  const [chartWidth, setChartWidth] = useState(DEFAULT_CHART_WIDTH);
  const datasets = series.map(item => item.data);
  const domain = useMemo(
    () => getChartDomain(datasets, startTimestamp, endTimestamp),
    [datasets, startTimestamp, endTimestamp],
  );
  const plotWidth = Math.max(1, chartWidth - PADDING.left - PADDING.right);

  useEffect(() => {
    const element = plotContainerRef.current;

    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }

    const updateWidth = () => {
      const nextWidth = Math.round(element.getBoundingClientRect().width);

      if (nextWidth > 0) {
        setChartWidth(nextWidth);
      }
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const chartModel = useMemo(() => {
    if (!domain) {
      return null;
    }

    const xScale = (value: string): number => {
      const time = parseTime(value);
      const ratio = (clamp(time, domain.minTime, domain.maxTime) - domain.minTime) / (domain.maxTime - domain.minTime);

      return PADDING.left + ratio * plotWidth;
    };

    const yScale = (value: number): number => {
      const ratio = (value - domain.minValue) / (domain.maxValue - domain.minValue);

      return PADDING.top + (1 - ratio) * PLOT_HEIGHT;
    };

    const renderedSeries = series.map(item => {
      const history = buildLinePoints(item.data.history, xScale, yScale);
      const forecast = buildLinePoints(item.data.forecast, xScale, yScale);
      const linePoints = [...history, ...forecast];
      const anomalies = item.data.anomalies
        .map(anomaly => {
          const pointOnLine = getAnomalyPointOnLine(anomaly.Date, linePoints, xScale);

          return pointOnLine
            ? {
                ...anomaly,
                x: pointOnLine.x,
                y: pointOnLine.y,
              }
            : null;
        })
        .filter((anomaly): anomaly is ITrendPointWithPosition => anomaly !== null);

      return {
        key: item.key,
        label: item.label,
        color: item.color,
        history,
        forecast,
        anomalies,
        forecastStart: forecast[0]?.x ?? null,
      };
    });

    return {
      extensionBoundaryX: extensionBoundaryTimestamp
        ? (() => {
            const offset = getForecastTimestampOffset(extensionBoundaryTimestamp, startTimestamp, endTimestamp);

            return offset === null ? null : PADDING.left + (offset / 100) * plotWidth;
          })()
        : null,
      yTicks: buildYTicks(domain.minValue, domain.maxValue),
      renderedSeries,
      xTicks: buildChronogramTicks(startTimestamp, endTimestamp),
      yScale,
    };
  }, [chartWidth, domain, endTimestamp, extensionBoundaryTimestamp, plotWidth, series, startTimestamp]);

  if (!chartModel) {
    return (
      <EmptyPlaceholder
        icon={<QueryStats />}
        text={formatMessage({ id: "pages.forecasting.selectedMeasureTrendEmpty" })}
        wrapperProps={{ sx: { minHeight: 190, backgroundColor: "#fafaff" } }}
      />
    );
  }

  const firstForecastStart = chartModel.renderedSeries[0]?.forecastStart ?? null;
  const forecastRegionStart = chartModel.extensionBoundaryX ?? (!showSeriesLegend ? firstForecastStart : null);

  return (
    <Box sx={{ py: 1.25 }}>
      {showSeriesLegend ? (
        <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mb: 1, px: 1.5 }}>
          {chartModel.renderedSeries.map(item => (
            <LegendItem key={item.key} color={item.color} label={item.label} />
          ))}
        </Stack>
      ) : null}
      <Box sx={FORECAST_TIMELINE_GRID_SX}>
        <Box
          sx={{
            width: CHRONOGRAM_NAME_CELL_WIDTH,
            position: "relative",
            height: CHART_HEIGHT,
            flexShrink: 0,
          }}
        >
          {chartModel.yTicks.map(value => {
            const y = chartModel.yScale(value);

            return (
              <Typography
                key={`y-label-${value}`}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: y,
                  transform: "translateY(-50%)",
                  fontSize: 11,
                  lineHeight: "14px",
                  color: "#303544",
                  whiteSpace: "nowrap",
                }}
              >
                {formatNumber(value, { maximumFractionDigits: 2 })}
              </Typography>
            );
          })}
        </Box>
        <Box sx={{ ...FORECAST_TIMELINE_PLOT_CELL_SX, minWidth: 0 }}>
          <Box ref={plotContainerRef} sx={{ minWidth: 0, position: "relative" }}>
            <svg
              aria-label={ariaLabel}
              height={CHART_HEIGHT}
              role="img"
              viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT}`}
              width={chartWidth}
              style={{ display: "block", maxWidth: "100%" }}
            >
              <defs>
                <pattern id={forecastPatternId} patternUnits="userSpaceOnUse" width="18" height="18">
                  <rect width="18" height="18" fill="rgba(46, 125, 50, 0.11)" />
                  <path d="M-4 18 L18 -4 M0 22 L22 0" stroke="rgba(46, 125, 50, 0.16)" strokeWidth="7" />
                </pattern>
              </defs>
              <rect
                x={PADDING.left}
                y={PADDING.top}
                width={plotWidth}
                height={PLOT_HEIGHT}
                fill="rgba(76, 175, 80, 0.2)"
              />
              {forecastRegionStart !== null ? (
                <>
                  <rect
                    x={forecastRegionStart}
                    y={PADDING.top}
                    width={Math.max(0, PADDING.left + plotWidth - forecastRegionStart)}
                    height={PLOT_HEIGHT}
                    fill={`url(#${forecastPatternId})`}
                  />
                  {chartModel.extensionBoundaryX === null ? (
                    <line
                      x1={forecastRegionStart}
                      x2={forecastRegionStart}
                      y1={PADDING.top}
                      y2={PADDING.top + PLOT_HEIGHT}
                      stroke="#173a8a"
                      strokeWidth={3}
                    />
                  ) : null}
                </>
              ) : null}
              {chartModel.xTicks.map(tick => {
                const x = PADDING.left + (tick.left / 100) * plotWidth;

                return (
                  <line
                    key={tick.key}
                    x1={x}
                    x2={x}
                    y1={PADDING.top}
                    y2={PADDING.top + PLOT_HEIGHT}
                    stroke="#1d6ff2"
                    strokeDasharray="2 4"
                    strokeWidth={0.75}
                  />
                );
              })}
              {chartModel.yTicks.map(value => {
                const y = chartModel.yScale(value);

                return (
                  <line
                    key={`y-${value}`}
                    x1={PADDING.left}
                    x2={PADDING.left + plotWidth}
                    y1={y}
                    y2={y}
                    stroke="#1d6ff2"
                    strokeDasharray="2 4"
                    strokeWidth={0.75}
                  />
                );
              })}
              <rect
                x={PADDING.left}
                y={PADDING.top}
                width={plotWidth}
                height={PLOT_HEIGHT}
                fill="none"
                stroke="#9aa3b2"
              />
              {chartModel.renderedSeries.map(item => (
                <g key={item.key}>
                  {item.history.length > 1 ? (
                    <polyline
                      points={getPolylinePoints(item.history)}
                      fill="none"
                      stroke={item.color}
                      strokeWidth={3}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  ) : null}
                  {item.history.length === 1 ? (
                    <circle cx={item.history[0].x} cy={item.history[0].y} r={4} fill={item.color} />
                  ) : null}
                  {item.forecast.length > 1 ? (
                    <polyline
                      points={getPolylinePoints(item.forecast)}
                      fill="none"
                      stroke={item.color}
                      strokeWidth={3}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      strokeDasharray="8 5"
                      opacity={0.85}
                    />
                  ) : null}
                  {item.forecast.length === 1 ? (
                    <circle cx={item.forecast[0].x} cy={item.forecast[0].y} r={4} fill={item.color} opacity={0.85} />
                  ) : null}
                  {item.anomalies.map((anomaly, index) => (
                    <circle
                      key={`${item.key}-${anomaly.Date}-${index}`}
                      cx={anomaly.x}
                      cy={anomaly.y}
                      r={7}
                      fill={getAnomalyStripeColor(anomaly.Value)}
                      stroke={item.color}
                      strokeWidth={2}
                      pointerEvents="none"
                    />
                  ))}
                </g>
              ))}
              {chartModel.extensionBoundaryX !== null ? (
                <line
                  x1={chartModel.extensionBoundaryX}
                  x2={chartModel.extensionBoundaryX}
                  y1={PADDING.top}
                  y2={PADDING.top + PLOT_HEIGHT}
                  stroke={FORECAST_EXTENSION_BOUNDARY_COLOR}
                  strokeWidth={3}
                  pointerEvents="none"
                />
              ) : null}
            </svg>
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 8,
                height: 14,
                pointerEvents: "none",
              }}
            >
              {chartModel.xTicks.map((tick, index, ticks) => (
                <Typography
                  key={tick.key}
                  sx={{
                    position: "absolute",
                    top: 0,
                    ...AXIS_LABEL_SX,
                    ...getXAxisTickLabelSx(index, ticks.length, tick.left),
                  }}
                >
                  {tick.label}
                </Typography>
              ))}
            </Box>
            {chartModel.renderedSeries.flatMap(item =>
              item.anomalies.map((anomaly, index) => {
                const tooltipTitle = `${item.label}: ${formatMessage({
                  id: "pages.forecasting.selectedMeasureTrendAnomaly",
                })} ${formatDate(anomaly.Date)} - ${formatNumber(anomaly.Value, { maximumFractionDigits: 3 })}`;

                return (
                  <Tooltip
                    key={`${item.key}-${anomaly.Date}-${index}`}
                    title={tooltipTitle}
                    arrow
                    placement="top"
                    disableInteractive
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        left: `${(anomaly.x / chartWidth) * 100}%`,
                        top: `${(anomaly.y / CHART_HEIGHT) * 100}%`,
                        width: 16,
                        height: 16,
                        transform: "translate(-50%, -50%)",
                        cursor: "pointer",
                      }}
                    />
                  </Tooltip>
                );
              }),
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const MeasureTrendCard: FC<{
  children: ReactNode;
  headerLegend?: ReactNode;
  measurePath: string | null;
  title: ReactNode;
}> = ({ children, headerLegend, measurePath, title }) => (
  <Box
    sx={{
      border: "1px solid #d7dde9",
      backgroundColor: "#ffffff",
      minHeight: 220,
    }}
  >
    <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #e4e8f1", backgroundColor: "#f8fafc" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
          <Typography sx={{ fontSize: 15, lineHeight: "20px", fontWeight: 600, color: "#202534" }}>{title}</Typography>
          {measurePath ? (
            <Typography
              sx={{
                mt: 0.25,
                fontSize: 11,
                lineHeight: "14px",
                color: "#5f6472",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={measurePath}
            >
              {measurePath}
            </Typography>
          ) : null}
        </Box>
        {headerLegend ? (
          <Stack
            direction="row"
            spacing={1.5}
            useFlexGap
            sx={{ flex: "0 1 auto", flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}
          >
            {headerLegend}
          </Stack>
        ) : null}
      </Stack>
    </Box>
    {children}
  </Box>
);

export const SelectedTrendsSection: FC<{
  combineAllTrendsInOneChart: boolean;
  combineTciTrendsInOneChart: boolean;
  endTimestamp: string | Date;
  extensionBoundaryTimestamp: string | Date | null;
  formatDate: (date: Date | string) => string;
  formatNumber: IntlShape["formatNumber"];
  isTrainTciTrendPinned: boolean;
  trendDataByKey: Record<string, IAnomalyMeasureTrendData>;
  onCombineAllTrendsInOneChartChange: (value: boolean) => void;
  onCombineTciTrendsInOneChartChange: (value: boolean) => void;
  selectedTrends: ISelectedForecastTrend[];
  startTimestamp: string | Date;
}> = ({
  combineAllTrendsInOneChart,
  combineTciTrendsInOneChart,
  endTimestamp,
  extensionBoundaryTimestamp,
  formatDate,
  formatNumber,
  isTrainTciTrendPinned,
  trendDataByKey,
  onCombineAllTrendsInOneChartChange,
  onCombineTciTrendsInOneChartChange,
  selectedTrends,
  startTimestamp,
}) => {
  const { formatMessage } = useIntl();
  const tciLabel = formatMessage({ id: "pages.forecasting.summary.tci" });
  const pinnedTrainTciTrendKey = isTrainTciTrendPinned
    ? selectedTrends.find(trend => trend.objectType === "train")?.key
    : null;

  const renderableTrends = useMemo(
    () => getRenderableForecastTrends(selectedTrends, trendDataByKey, pinnedTrainTciTrendKey),
    [pinnedTrainTciTrendKey, selectedTrends, trendDataByKey],
  );

  const trendSeries = useMemo(
    () =>
      renderableTrends.map((trend, index) => {
        const data = trendDataByKey[trend.key] ?? {
          metadata: null,
          history: [],
          forecast: [],
          anomalies: [],
        };

        return {
          key: trend.key,
          label: getTrendTitle(data, trend, tciLabel),
          color: MEASURE_TREND_COLORS[index % MEASURE_TREND_COLORS.length],
          data,
          trend,
        };
      }),
    [renderableTrends, tciLabel, trendDataByKey],
  );

  const trendSeriesGroups = useMemo(() => {
    const trendSeriesByKey = new Map(trendSeries.map(item => [item.key, item]));

    return groupForecastTrends(
      renderableTrends,
      combineTciTrendsInOneChart,
      combineAllTrendsInOneChart,
      isTrainTciTrendPinned,
    ).map(group => group.flatMap(trend => (trendSeriesByKey.has(trend.key) ? [trendSeriesByKey.get(trend.key)!] : [])));
  }, [combineAllTrendsInOneChart, combineTciTrendsInOneChart, isTrainTciTrendPinned, renderableTrends, trendSeries]);

  if (selectedTrends.length === 0) {
    return (
      <Box sx={{ mt: 1.5 }}>
        <MeasureTrendCard title={<FormattedMessage id="pages.forecasting.selectedTrendsTitle" />} measurePath={null}>
          <EmptyPlaceholder
            icon={<QueryStats />}
            text={formatMessage({ id: "pages.forecasting.selectedTrendPlaceholder" })}
            wrapperProps={{ sx: { minHeight: 190, backgroundColor: "#fafaff" } }}
          />
        </MeasureTrendCard>
      </Box>
    );
  }

  if (trendSeries.length === 0) {
    return null;
  }

  const renderCombinedTrendCard = (group: (typeof trendSeries)[number][]): ReactNode => {
    const containsOnlyTci = group.every(item => isTciForecastTrend(item.trend));
    const containsOnlyMeasures = group.every(item => !isTciForecastTrend(item.trend));
    const titleId = containsOnlyTci
      ? "pages.forecasting.combinedTciTrendTitle"
      : containsOnlyMeasures
        ? "pages.forecasting.combinedMeasureTrendTitle"
        : "pages.forecasting.combinedTrendTitle";

    return (
      <MeasureTrendCard
        key={`combined:${group.map(item => item.key).join("|")}`}
        title={<FormattedMessage id={titleId} />}
        measurePath={null}
      >
        <MeasureTrendChart
          ariaLabel={formatMessage({ id: titleId })}
          endTimestamp={endTimestamp}
          extensionBoundaryTimestamp={extensionBoundaryTimestamp}
          formatDate={formatDate}
          formatMessage={formatMessage}
          formatNumber={formatNumber}
          series={group.map(item => ({
            key: item.key,
            label: item.label,
            color: item.color,
            data: item.data,
          }))}
          showSeriesLegend
          startTimestamp={startTimestamp}
        />
      </MeasureTrendCard>
    );
  };

  const renderIndividualTrendCard = (item: (typeof trendSeries)[number]): ReactNode => {
    const hasData = hasTrendLineData(item.data);
    const isTci = isTciForecastTrend(item.trend);

    return (
      <MeasureTrendCard
        key={item.key}
        title={
          <>
            <FormattedMessage
              id={isTci ? "pages.forecasting.tciTrendTitle" : "pages.forecasting.selectedMeasureTrendTitle"}
            />
            {isTci ? `: ${item.trend.name}` : item.label ? `: ${item.label}` : ""}
          </>
        }
        measurePath={isTci ? null : getMeasurePath(item.data)}
        headerLegend={
          hasData ? (
            <>
              {item.data.history.length > 0 ? (
                <LegendItem
                  color={item.color}
                  label={<FormattedMessage id="pages.forecasting.selectedMeasureTrendHistory" />}
                />
              ) : null}
              {item.data.forecast.length > 0 ? (
                <LegendItem
                  color={item.color}
                  dashed
                  label={<FormattedMessage id="pages.forecasting.selectedMeasureTrendForecast" />}
                />
              ) : null}
              {item.data.anomalies.length > 0 ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#f44336" }} />
                  <Typography sx={{ fontSize: 11, lineHeight: "14px", color: "#5f6472" }}>
                    <FormattedMessage id="pages.forecasting.selectedMeasureTrendAnomaly" />
                  </Typography>
                </Box>
              ) : null}
            </>
          ) : null
        }
      >
        {hasData ? (
          <MeasureTrendChart
            ariaLabel={item.label || formatMessage({ id: "pages.forecasting.selectedMeasureTrendTitle" })}
            endTimestamp={endTimestamp}
            extensionBoundaryTimestamp={extensionBoundaryTimestamp}
            formatDate={formatDate}
            formatMessage={formatMessage}
            formatNumber={formatNumber}
            series={[
              {
                key: item.key,
                label: item.label,
                color: item.color,
                data: item.data,
              },
            ]}
            startTimestamp={startTimestamp}
          />
        ) : (
          <EmptyPlaceholder
            icon={<QueryStats />}
            text={formatMessage({ id: "pages.forecasting.selectedMeasureTrendEmpty" })}
            wrapperProps={{ sx: { minHeight: 190, backgroundColor: "#fafaff" } }}
          />
        )}
      </MeasureTrendCard>
    );
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ mb: 1 }}>
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
          <FormControlLabel
            control={
              <Checkbox
                checked={combineTciTrendsInOneChart}
                onChange={event => onCombineTciTrendsInOneChartChange(event.target.checked)}
                size="small"
              />
            }
            label={
              <Typography sx={{ fontSize: 13, color: "#363846" }}>
                <FormattedMessage id="pages.forecasting.combineTciTrendsInOneChart" />
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={combineAllTrendsInOneChart}
                onChange={event => onCombineAllTrendsInOneChartChange(event.target.checked)}
                size="small"
              />
            }
            label={
              <Typography sx={{ fontSize: 13, color: "#363846" }}>
                <FormattedMessage id="pages.forecasting.combineAllTrendsInOneChart" />
              </Typography>
            }
          />
        </Stack>
      </Box>

      <Stack spacing={1.5}>
        {trendSeriesGroups.flatMap(group => {
          const isTciGroup = group.every(item => isTciForecastTrend(item.trend));
          const renderAsCombined = combineAllTrendsInOneChart || (combineTciTrendsInOneChart && isTciGroup);

          return renderAsCombined ? [renderCombinedTrendCard(group)] : group.map(renderIndividualTrendCard);
        })}
      </Stack>
    </Box>
  );
};
